import React, { useEffect, useState } from 'react';
import {
  Alert,
  Typography,
  notification,
  Form,
  Select,
  Input,
  Descriptions,
  Skeleton,
  Card,
  Modal,
  Divider,
} from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import CustomButton from '../../../components/CustomButton';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import TermsForm from '../../../containers/TermForm';
import {
  catchException,
  getLocalStorage,
  useQuery,
} from '../../../utils/helpers/helpers';

import INTERNAL_LINKS from '../../../enums/InternalLinks';
import MESSAGES from '../../../enums/Messages';

import {
  getProjectInfo as getProject,
  post,
  put,
  get,
  sendToContract,
} from '../../../api/module/terms';

import {
  sendObservations,
  getByTerm as getTermComment,
  getUsersAssign,
} from '../../../api/module/term_comments';

import './style.scss';

const { Title, Paragraph } = Typography;

const moduleTitles = {
  A: 'A. UBICACIÓN INSTITUCIONAL',
  B: 'B. PERFIL REQUERIDO',
  C: 'C. ESPECIFICACIONES DEL CONTRATO',
  D: 'D. ASPECTOS JURÍDICOS Y ECONÓMICOS',
  E: 'E. PERSONA A CONTRATAR / DONATARIO',
  F: 'F. DOCUMENTOS REQUERIDOS',
  G: 'G. DOCUMENTOS COMPLEMENTARIOS',
};

const TermsPage = ({ match, history, sendPerm }) => {
  let query = useQuery();
  const show = query.get('show') === 'true';
  const [isLoading, setLoading] = useState(false);
  const [termId] = useState(parseInt(match.params.id));
  const [disabled] = useState(query.get('show') === 'true');
  const [projectId] = useState(parseInt(match.params.project_id));
  const [contractId] = useState(parseInt(match.params.contract_id));
  const [term, setTerm] = useState(null);
  const [groups, setGroups] = useState(null);
  const [users, setUsers] = useState(null);
  const [statusOptions, setStatusOptions] = useState(null);
  const [observations, setObservations] = useState(null);
  const [project, setProject] = useState(null);
  const [errors, setErrors] = useState();

  const [formTerm] = Form.useForm();
  const [formObservations] = Form.useForm();
  const [loadingObservations, setLoadingObservation] = useState(false);
  const [loadingTerm, setLoadingTerm] = useState(false);

  const [currentModule, setCurrentModule] = useState(query.get('module'));

  const breadcrumbItems = [
    {
      text: 'Términos de referencia',
      to: `${INTERNAL_LINKS.TDR_HOME}/${projectId}`,
    },
    {
      text: 'Formulario de Términos de referencia',
    },
  ];

  useEffect(() => {
    if (project == null) {
      getProjectData();
    }

    if (term == null) {
      if (termId > 0) {
        getTermData();
      } else {
        setTerm({
          id: 0,
          responsable: parseInt(getLocalStorage('userId')),
        });
      }
    }
    getObservation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProjectData = async () => {
    const response = await getProject(projectId);
    setProject(response.data);
  };

  const getUsersData = async (id) => {
    formObservations.setFieldsValue({
      usuario_destino: undefined,
    });
    setUsers(null);
    try {
      const response = await getUsersAssign(id, projectId);
      setUsers(response.data.results);
    } catch (error) {
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_GET_MSG,
        duration: 2,
      });
    }
  };

  const sendObservation = async (data, ignoreDocs = false) => {
    if (!ignoreDocs && (term?.documentos_adicionales || []).length < 3) {
      Modal.confirm({
        title: '¿Los documentos complementarios están completos?',
        okText: 'Sí',
        cancelText: 'No',
        onOk: () => {
          sendObservation(data, true);
        },
      });
      return;
    }

    setLoadingObservation(true);

    if (formTerm.isFieldsTouched()) {
      try {
        const formData = await formTerm.validateFields();
        await save(formData, false, false);
      } catch (error) {
        setLoadingObservation(false);
        return;
      }
    }

    try {
      const response = await sendObservations(termId, data);
      if (!checkValidation(response, false)) {
        setLoadingObservation(false);
        Modal.confirm({
          title: 'Existen errores de validación',
          content: (
            <>
              <p>Hay errores en el TDR.</p>
              <>
                {Object.entries(
                  response.data.errors.reduce((pv, cv) => {
                    if (cv.module in pv) pv[cv.module].push(cv.error);
                    else pv[cv.module] = [cv.error];
                    return pv;
                  }, {}),
                ).map((v) => (
                  <>
                    <Divider orientation="left">{moduleTitles[v[0]]}</Divider>
                    <ul>
                      {v[1].map((item) => (
                        <li>{item}</li>
                      ))}
                    </ul>
                  </>
                ))}
              </>
              <p>¿Desea enviar de todos modos?</p>
            </>
          ),
          width: '80%',
          okText: 'Si',
          cancelText: 'No',
          okType: 'default',
          okButtonProps: {
            className: 'background-wonderlust',
          },
          onOk: async () => await onSkipValidation(data),
        });
      } else {
        formObservations.resetFields();
        notification.success({
          message: MESSAGES.SUCCESS_MSG,
          description: MESSAGES.DESC_SUCCESS_MSG,
          placement: 'bottomRight',
        });
        setTimeout(() => {
          history.push(`${INTERNAL_LINKS.TDR_HOME}/${projectId}`);
        }, 1000);
      }
    } catch (err) {
      setLoadingObservation(false);
      catchException(err, MESSAGES.DESC_ERROR_POST_MSG, MESSAGES.TIME_SECONDS, {
        placement: 'bottomRight',
      });
    }
  };

  const getObservation = async () => {
    if (termId > 0) {
      const response = await getTermComment(termId);
      setGroups(response.data.groups);
      setStatusOptions(response.data.statuses);
      setObservations(response.data.observations);
    }
  };

  const getTermData = async () => {
    setLoadingTerm(true);
    let response = await get(termId);
    const termData = response.data;

    setLoadingTerm(false);
    if (termData['fecha_inicio'] != null) {
      termData['fecha_inicio'] = dayjs(termData['fecha_inicio']);
    }
    if (termData['fecha_fin'] != null) {
      termData['fecha_fin'] = dayjs(termData['fecha_fin']);
    }
    if (termData['fecha_maxima_inicio'] != null) {
      termData['fecha_maxima_inicio'] = dayjs(termData['fecha_maxima_inicio']);
    }
    if (termData['fecha_maxima_fin'] != null) {
      termData['fecha_maxima_fin'] = dayjs(termData['fecha_maxima_fin']);
    }
    if (termData['pais'] == null) {
      termData['pais'] = 45;
    }
    if (termData['estado'] == null && termData['pais'] === 45) {
      termData['estado'] = 3;
    }
    if (termData['ciudad'] == null && termData['estado'] === 3) {
      termData['ciudad'] = 149;
    }
    if (termData['moneda'] == null) {
      termData['moneda'] = 1;
    }
    if (termData.fechas_fijas != null) {
      termData['fechas_fijas'] = termData.fechas_fijas === true ? 1 : 0;
    }
    if (termData.cargo != null && termData.cargo !== undefined) {
      termData.cargo = parseInt(termData.cargo);
    }
    setTerm(termData);
  };

  const prepareData = (data) => {
    if (termId === 0) {
      if (data['fecha_inicio'] != null) {
        data['fecha_inicio'] = dayjs(data.fecha_inicio).format('YYYY-MM-DD');
      }
      if (data['fecha_fin'] != null) {
        data['fecha_fin'] = dayjs(data.fecha_fin).format('YYYY-MM-DD');
      }
      if (data['fecha_maxima_inicio'] != null) {
        data['fecha_maxima_inicio'] = dayjs(data.fecha_maxima_inicio).format(
          'YYYY-MM-DD',
        );
      }
      if (data['fecha_maxima_fin'] != null) {
        data['fecha_maxima_fin'] = dayjs(data.fecha_maxima_fin).format(
          'YYYY-MM-DD',
        );
      }
      if (data['valor_total'] == null) {
        data['valor_total'] = 0;
      }
      data['proyecto'] = projectId;
      data['tipo_contrato'] = contractId;
      data['estado_proceso'] = 1;
    } else {
      if (data['fecha_inicio'] != null && data['fecha_inicio'] != undefined) {
        data['fecha_inicio'] = dayjs(data.fecha_inicio).format('YYYY-MM-DD');
      }
      if (data['fecha_fin'] != null && data['fecha_fin'] != undefined) {
        data['fecha_fin'] = dayjs(data.fecha_fin).format('YYYY-MM-DD');
      }

      if (
        data['fecha_maxima_inicio'] != null &&
        data['fecha_maxima_inicio'] != undefined
      ) {
        data['fecha_maxima_inicio'] = dayjs(data.fecha_maxima_inicio).format(
          'YYYY-MM-DD',
        );
      }
      if (
        data['fecha_maxima_fin'] != null &&
        data['fecha_maxima_fin'] != undefined
      ) {
        data['fecha_maxima_fin'] = dayjs(data.fecha_maxima_fin).format(
          'YYYY-MM-DD',
        );
      }
    }
    return data;
  };

  const reloadPage = (pk) => {
    setTimeout(() => {
      window.location = `${INTERNAL_LINKS.TERM}/${pk}/${projectId}/${contractId}?module=${currentModule}`;
    }, 500);
    notification.success({
      message: MESSAGES.SUCCESS_MSG,
      description: MESSAGES.DESC_SUCCESS_MSG,
      placement: 'bottomRight',
    });
  };

  const checkValidation = (response, throwException = true) => {
    if (!response.data.valid) {
      setErrors(
        response.data.errors.reduce((pv, cv) => {
          if (cv.module in pv) pv[cv.module].push(cv.error);
          else pv[cv.module] = [cv.error];
          return pv;
        }, {}),
      );
      if (throwException) throw Error('La validación fallo');
      else return false;
    }
    return true;
  };

  const onSkipValidation = async (data) => {
    try {
      setLoadingObservation(true);
      data['skip_validation'] = true;
      await sendObservations(termId, data);
      formObservations.resetFields();
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        placement: 'bottomRight',
      });
      setTimeout(() => {
        history.push(`${INTERNAL_LINKS.TDR_HOME}/${projectId}`);
      }, 1000);
    } catch (err) {
      setLoadingObservation(false);
      catchException(err, MESSAGES.DESC_ERROR_POST_MSG, MESSAGES.TIME_SECONDS, {
        placement: 'bottomRight',
      });
    }
  };

  const save = async (data, review = false, reloadAfter = true) => {
    try {
      setErrors();
      setLoading(true);
      let response;
      const preparedData = prepareData(data);

      if (termId === 0) {
        response = await post(preparedData);
        if (reloadAfter) reloadPage(response.data.id);
        else setLoading(false);
        return;
      }

      if (review) {
        response = await sendToContract(termId, preparedData);
        checkValidation(response, true);
        setLoading(false);
        notification.success({
          message: MESSAGES.SUCCESS_MSG,
          description: MESSAGES.DESC_SUCCESS_MSG,
          placement: 'bottomRight',
        });
        setTimeout(() => {
          history.push(`${INTERNAL_LINKS.TDR_HOME}/${projectId}`);
        }, 1000);
      } else {
        response = await put(termId, preparedData);
        if (reloadAfter) reloadPage(response.data.id);
        else setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      catchException(
        err,
        MESSAGES.DESC_ERROR_POST_MSG,
        MESSAGES.TIME_SECONDS,
        {
          placement: 'bottomRight',
        },
        0.05,
      );
      if (!reloadAfter) throw err;
    }
  };

  return (
    <div className="terms-page-content">
      <BreadcrumbContainer items={breadcrumbItems} />
      <div className="box-title">
        <Title>Términos de referencia (TDR)</Title>
        <Title level={3}>Proyecto: {project && project.nombre}</Title>
      </div>
      <div className="box-description">
        <Paragraph>
          Los siguientes términos de referencia complementan el texto del
          contrato firmado entre las partes y están subordinados a dicho
          contrato.
        </Paragraph>
      </div>
      <Skeleton loading={loadingTerm} active>
        {term != null && project != null && (
          <TermsForm
            form={formTerm}
            project={project}
            key={termId}
            term={term}
            project_id={projectId}
            contract_id={contractId}
            onSave={save}
            history={history}
            show={show}
            disabled={disabled}
            isLegal={sendPerm}
            currentModule={currentModule}
            setCurrentModule={setCurrentModule}
            loading={isLoading}
          />
        )}
        {errors && (
          <Alert
            message="Errores de validación de TDR:"
            type="error"
            showIcon
            style={{ marginBottom: '2rem' }}
            description={
              <>
                {Object.entries(errors).map((v) => (
                  <>
                    <Divider orientation="left">{moduleTitles[v[0]]}</Divider>
                    <ul>
                      {v[1].map((item) => (
                        <li>{item}</li>
                      ))}
                    </ul>
                  </>
                ))}
              </>
            }
          />
        )}
        {term && termId > 0 && (
          <>
            <Alert
              showIcon
              type="info"
              message="Información importante"
              description="Antes de continuar por favor guarde el TDR"
            />
            <Card
              size={'small'}
              bordered={false}
              title={<Title level={3}>ASIGNACIÓN DE ESTADOS</Title>}
            >
              <Form
                form={formObservations}
                layout="vertical"
                onFinish={(data) => {
                  sendObservation(data);
                }}
              >
                <Form.Item
                  label="Selección el rol que revisará"
                  name="rol"
                  rules={[{ required: true }]}
                >
                  <Select
                    onChange={(value) => {
                      getUsersData(value);
                    }}
                  >
                    {groups &&
                      groups.map((item) => {
                        return (
                          <Select.Option value={item.id}>
                            {item.name}
                          </Select.Option>
                        );
                      })}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Selección de la persona que revisará"
                  name="usuario_destino"
                  rules={[{ required: true }]}
                >
                  <Select options={users} />
                </Form.Item>
                <Form.Item
                  label="Estado para"
                  name="estado_termino"
                  rules={[{ required: true }]}
                >
                  <Select options={statusOptions} />
                </Form.Item>
                <Form.Item label="Observaciones" name="observacion">
                  <Input.TextArea />
                </Form.Item>
                <Form.Item>
                  <CustomButton
                    loading={loadingObservations}
                    htmlType="submit"
                    text="Enviar"
                  />
                </Form.Item>
              </Form>
              {observations && (
                <Card
                  size={'small'}
                  bordered={false}
                  title={
                    <Title level={4}>Histórico de estados asignados</Title>
                  }
                >
                  {observations.map((item, index) => (
                    <Descriptions
                      title={`Estado No. ${index + 1}`}
                      layout="vertical"
                      column={4}
                      bordered
                    >
                      <Descriptions.Item label="Usuario que realiza el envío y observación">
                        {item.nombre_completo}
                      </Descriptions.Item>
                      <Descriptions.Item label="Estado Asignado">
                        {item.estado_termino_nombre}
                      </Descriptions.Item>
                      <Descriptions.Item label="Enviado a">
                        {item.rol_nombre}
                      </Descriptions.Item>
                      <Descriptions.Item label="Fecha y hora del envío">
                        {dayjs(item.created_at).format('DD MMMM YYYY HH:mm')}
                      </Descriptions.Item>
                      <Descriptions.Item label="Observación" span={4}>
                        {item.observacion}{' '}
                      </Descriptions.Item>
                    </Descriptions>
                  ))}
                </Card>
              )}
            </Card>
          </>
        )}
      </Skeleton>
    </div>
  );
};

export default TermsPage;
